import React, { useState } from 'react';
import { Check, Minus, HelpCircle } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import StocksFAQ from './StocksFAQ';
import BackButton from '../BackButton';

const StocksPricing = () => {
  const [activeTooltip, setActiveTooltip] = useState(null);

  // Define the feature rows to ensure consistency with tooltip content
  const featureRows = [
    { 
      label: "Account type", 
      type: "text",
      tooltip: "The type of trading account offered under this plan with varying levels of access and support."
    },
    
    { 
      label: "Trading platform access", 
      type: "text",
      tooltip: "Level of access to our trading platform, including features and tools available to you."
    },
    { 
      label: "Stock recommendations", 
      type: "check",
      tooltip: "Personalized stock recommendations based on market analysis and your investment goals."
    },
    { 
      label: "Portfolio construction guidance", 
      type: "check",
      tooltip: "Expert guidance on building a balanced portfolio aligned with your risk profile and objectives."
    },
    { 
      label: "Quarterly portfolio review", 
      type: "check",
      tooltip: "Regular review of your portfolio performance with recommendations for adjustments."
    },
    { 
      label: "Stock monitoring alerts", 
      type: "check",
      tooltip: "Timely alerts about significant price movements or news affecting your holdings."
    },
    { 
      label: "Sector allocation strategy", 
      type: "check",
      tooltip: "Strategic advice on distributing investments across different market sectors for optimal diversification."
    },
    { 
      label: "Risk management consultation", 
      type: "check",
      tooltip: "Professional consultation on managing and mitigating investment risks in your portfolio."
    },
    { 
      label: "Tax harvesting guidance", 
      type: "check",
      tooltip: "Strategies to optimize your tax position through strategic buying and selling of securities."
    },
    { 
      label: "Research reports access", 
      type: "check",
      tooltip: "Access to comprehensive research reports on companies, sectors, and market trends."
    },
    { 
      label: "Technical analysis", 
      type: "check",
      tooltip: "Advanced analysis of price patterns and market trends to identify potential trading opportunities."
    },
    { 
      label: "Fundamental analysis", 
      type: "check",
      tooltip: "In-depth analysis of a company's financial health, business model, and growth prospects."
    },
    { 
      label: "Market insights newsletter", 
      type: "check",
      tooltip: "Regular newsletter with expert commentary on market movements and investment opportunities."
    },
  ];

  // Define the plan data
  const plans = [
    {
      name: "Basic Plan",
      headline: "₹0",
      subtext: "/month",
      buttonText: "Coming Soon",
      isPrimary: false,
      description: "Zero-fee basic trading with essential market updates",
      values: [
        "Self-directed trading",
        "Basic platform",
        false, false, false, true, false, false, false, false, false, true, true
      ]
    },
    {
      name: "Advisory Plan",
      headline: "₹6250",
      subtext: "/month",
      buttonText: "Schedule Meeting",
      isPrimary: true,
      description: "Professional guidance from SEBI registered investment advisor",
      values: [
        "SEBI RIA guided investing",
        "Advanced platform",
        true, true, true, true, true, true, true, true, true, true, true
      ]
    }
  ];

  // Handle tooltip toggle
  const toggleTooltip = (index) => {
    if (activeTooltip === index) {
      setActiveTooltip(null);
    } else {
      setActiveTooltip(index);
    }
  };

  return (
    <>
      <Header />
      <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6 lg:py-8">
        {/* Header Section */}
        <div className="text-center mb-8 sm:mb-12 pt-16">
          <BackButton to="/fee" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-900 mb-2 sm:mb-4 pt-12">Choose Your Stock Market Journey</h2>
          <p className="text-gray-600 text-sm sm:text-base">Select the plan that fits your trading and investment style</p>
        </div>

        {/* Pricing Table with horizontal scroll for small/medium screens */}
        <div className="relative mb-8 sm:mb-12 max-w-6xl mx-auto">
          {/* Scroll indicator for small/medium screens */}
          <div className="md:hidden text-xs text-gray-500 text-center mb-2 animate-pulse">
            ← Scroll horizontally to view all details →
          </div>
          
          {/* Table container with horizontal scroll */}
          <div className="overflow-x-auto pb-4">
            {/* Minimum width to ensure proper display on small screens */}
            <div className="min-w-[768px] border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="p-3 sm:p-4 text-left w-1/3 border border-gray-200">
                      <h3 className="text-base sm:text-lg font-semibold text-blue-900">Benefits</h3>
                    </th>
                    {plans.map((plan, index) => (
                      <th key={index} className={`p-3 sm:p-4 w-1/3 border border-gray-200 ${plan.isPrimary ? 'bg-blue-50' : ''}`}>
                        <h3 className="text-lg sm:text-xl font-bold mb-1 sm:mb-2 text-blue-900">{plan.name}</h3>
                        <div className="mb-4 sm:mb-6">
                          <span className="text-2xl sm:text-3xl font-bold text-blue-900">{plan.headline}</span>
                          <span className="text-gray-600 text-sm sm:text-base">{plan.subtext}</span>
                        </div>
                        {plan.buttonText === 'Schedule Meeting' ? (
                          <a
                            href="/meeting"
                            className={`w-full inline-block text-center py-1.5 sm:py-2 px-2 sm:px-4 rounded-md transition-colors mb-6 sm:mb-8 text-sm sm:text-base ${
                              plan.isPrimary 
                                ? 'bg-blue-900 text-white hover:bg-blue-800' 
                                : 'border border-blue-900 text-blue-900 hover:bg-blue-50'
                            }`}
                          >
                            {plan.buttonText}
                          </a>
                        ) : (
                          <button
                            className={`w-full py-1.5 sm:py-2 px-2 sm:px-4 rounded-md transition-colors mb-6 sm:mb-8 text-sm sm:text-base ${
                              plan.isPrimary 
                                ? 'bg-blue-900 text-white hover:bg-blue-800' 
                                : 'border border-blue-900 text-blue-900 hover:bg-blue-50'
                            }`}
                            disabled={plan.buttonText === 'Coming Soon'}
                          >
                            {plan.buttonText}
                          </button>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {featureRows.map((row, rowIndex) => (
                    <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="p-3 sm:p-4 border border-gray-200 text-sm sm:text-base relative">
                        <div className="flex items-center">
                          <button 
                            className="mr-2 text-gray-400 hover:text-blue-600 focus:outline-none"
                            onClick={() => toggleTooltip(rowIndex)}
                            onMouseEnter={() => setActiveTooltip(rowIndex)}
                            onMouseLeave={() => setActiveTooltip(null)}
                            aria-label={`Information about ${row.label}`}
                          >
                            <HelpCircle className="h-4 w-4" />
                          </button>
                          <span>{row.label}</span>
                          
                          {/* Tooltip content - positioned to the left */}
                          {activeTooltip === rowIndex && (
                            <div className="absolute z-10 left-8 -mt-2 w-64 sm:w-72 bg-white border border-gray-200 rounded-md shadow-lg p-3 text-xs sm:text-sm text-gray-700">
                              {row.tooltip}
                            </div>
                          )}
                        </div>
                      </td>
                      {plans.map((plan, planIndex) => (
                        <td 
                          key={planIndex} 
                          className={`p-3 sm:p-4 text-center border border-gray-200 ${plan.isPrimary ? 'bg-blue-50' : ''}`}
                        >
                          {row.type === "check" ? (
                            plan.values[rowIndex] ? 
                              <Check className="text-green-600 h-4 w-4 sm:h-5 sm:w-5 mx-auto" /> : 
                              <Minus className="text-gray-400 h-4 w-4 sm:h-5 sm:w-5 mx-auto" />
                          ) : (
                            <span className="text-sm sm:text-base">{plan.values[rowIndex]}</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr>
                    <td className="p-3 sm:p-4 border border-gray-200"></td>
                    {plans.map((plan, index) => (
                      <td key={index} className={`p-3 sm:p-4 text-center border border-gray-200 ${plan.isPrimary ? 'bg-blue-50 text-blue-700' : 'text-gray-600'}`}>
                        <div className="text-xs sm:text-sm">{plan.description}</div>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Scroll fade indicator on right edge for small screens */}
          <div className="md:hidden absolute top-0 right-0 bottom-0 w-12 bg-gradient-to-l from-white to-transparent pointer-events-none"></div>
        </div>
        
        <div className="max-w-6xl mx-auto">
          <StocksFAQ />
        </div>
      </div>
      <Footer />
    </>
  );
};

export default StocksPricing;